import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Icon, Badge, Tooltip, Modal, Form, Input, message, Spin } from 'antd';
import {
  CheckSquareIcon,
  UnopenedCircleIcon,
  DeliveringIcon,
  LegendQueuedIcon,
  LegendSentToSendGridIcon,
  LegendDeliveredIcon,
  LegendBouncedIcon
} from '../../common/CustomIcon';
import { fetchInvoiceEmailActivity } from '../../api/BillingScreenAPI';

// Helper to format timestamps to a human-readable format
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return timestamp;
  }
};

export const InvoiceEmailActivities = ({ 
  onResendAll, 
  onResendFailed, 
  accountInvoiceId, 
  organizationId,
  fetchCurrentInvoiceDetails,
  fetchCurrentInvoiceData,
  activeTab
}) => {

  const parseEmailActivity = (emailAct, index, existingExpandedState = {}) => {
    const email = emailAct.email || '';
    const attempts = emailAct.attempts || (emailAct.activities ? emailAct.activities.length : 1);
    const activities = emailAct.activities || [];
    
    // Sort activities by attempt number ascending
    const sortedActs = [...activities].sort((a, b) => (a.attempt || 0) - (b.attempt || 0));
    const lastAct = sortedActs[sortedActs.length - 1] || {};
    
    let deliveryStatus = 'unopened';
    let deliveryText = 'Delivered';
    let timestamp = '';
    let timestampColor = '#8c8c8c';
    
    if (lastAct.bounced_at) {
      deliveryStatus = 'bounced';
      deliveryText = 'Bounced';
      timestamp = `Bounced • ${formatDate(lastAct.bounced_at)}`;
      timestampColor = '#ed5857';
    } else if (lastAct.delivered_at || lastAct.delivery_at) {
      deliveryStatus = 'success';
      deliveryText = 'Delivered';
      const delTime = lastAct.delivered_at || lastAct.delivery_at;
      timestamp = `Delivered • ${formatDate(delTime)}`;
      timestampColor = '#228a50';
    } else if (lastAct.sent_at) {
      deliveryStatus = 'delivering';
      deliveryText = 'Delivering';
      timestamp = `Delivering • ${formatDate(lastAct.sent_at)}`;
      timestampColor = '#dc9e03';
    }
    
    const history = sortedActs.map(act => {
      const label = `Attempt ${act.attempt || 1}`;
      let detailParts = [];
      
      const sentTimeStr = act.sent_at ? `at ${formatDate(act.sent_at)}` : '';
      const sentThroughStr = act.sent_through ? `via ${act.sent_through}` : '';
      const sentByStr = act.sent_by ? `by ${act.sent_by}` : '';
      const sentDetails = [sentThroughStr, sentByStr, sentTimeStr].filter(Boolean).join(' ');
      if (sentDetails) {
        detailParts.push(`Sent ${sentDetails}`);
      }
      
      let attemptColor = '#8c8c8c';
      if (act.bounced_at) {
        detailParts.push(`Bounced at ${formatDate(act.bounced_at)}`);
        attemptColor = '#ed5857';
      } else if (act.delivered_at || act.delivery_at) {
        const delTime = act.delivered_at || act.delivery_at;
        detailParts.push(`Delivered at ${formatDate(delTime)}`);
        attemptColor = '#228a50';
      } else {
        detailParts.push('Delivering');
        attemptColor = '#dc9e03';
      }
      
      return {
        label,
        detail: detailParts.join(' — '),
        color: attemptColor
      };
    });

    const id = email;

    return {
      id,
      email,
      resent: attempts > 1,
      resentText: `Resent • Attempt ${attempts}`,
      pipeline: {
        sent: { status: 'success', text: 'Sent' },
        delivery: { status: deliveryStatus, text: deliveryText }
      },
      timestamp,
      timestampColor,
      showHistoryLogs: !!existingExpandedState[id],
      history,
      activities
    };
  };

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('Billing contact');

  const loadActivities = () => {
    if (!accountInvoiceId || !organizationId) return;
    setLoading(true);
    fetchInvoiceEmailActivity(organizationId, accountInvoiceId)
      .then((res) => {
        const emailActivities = res.email_activities || [];
        
        // Preserve existing expanded state for event histories
        const existingExpandedState = {};
        recipients.forEach(r => {
          if (r.showHistoryLogs) {
            existingExpandedState[r.id] = true;
          }
        });

        const parsed = emailActivities.map((emailAct, index) => parseEmailActivity(emailAct, index, existingExpandedState));
        setRecipients(parsed);
      })
      .catch((err) => {
        console.error("Error fetching email activities:", err);
        message.error("Failed to fetch email activities");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === 'Email Activities') {
      loadActivities();
    }
  }, [accountInvoiceId, organizationId, activeTab]);

  useEffect(() => {
    let timer;
    if (fetchCurrentInvoiceDetails) {
      loadActivities();
      timer = setTimeout(() => {
        loadActivities();
      }, 2000);
      if (fetchCurrentInvoiceData) {
        fetchCurrentInvoiceData(false);
      }
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [fetchCurrentInvoiceDetails]);

  // Triggered when "Resend to failed recipient" is clicked
  const handleResendFailed = () => {
    const bouncedEmails = recipients.filter(r => r.pipeline.delivery.status === 'bounced').map(r => r.email);
    if (onResendFailed) {
      onResendFailed(bouncedEmails);
    } else {
      setLoadingAction(true);
      message.loading({ content: 'Resending to failed recipient...', key: 'resend_act' });
      setTimeout(() => {
        setRecipients(prev => prev.map(rec => {
          if (rec.pipeline.delivery.status === 'bounced') {
            const nextAttempt = rec.history.length + 1;
            const nowStr = new Date().toISOString();
            return {
              ...rec,
              resent: true,
              resentText: `Resent • Attempt ${nextAttempt}`,
              pipeline: {
                sent: { status: 'success', text: 'Sent' },
                delivery: { status: 'success', text: 'Delivered' }
              },
              timestamp: `Delivered • Just now`,
              timestampColor: '#228a50',
              history: [
                ...rec.history,
                {
                  label: `Attempt ${nextAttempt}`,
                  detail: `Sent via manual by current_user at ${formatDate(nowStr)} — Delivered at ${formatDate(nowStr)}`,
                  color: '#228a50'
                }
              ],
              activities: [
                ...(rec.activities || []),
                {
                  attempt: nextAttempt,
                  sent_at: nowStr,
                  sent_through: 'manual',
                  sent_by: 'current_user',
                  delivered_at: nowStr
                }
              ]
            };
          }
          return rec;
        }));
        setLoadingAction(false);
        message.success({ content: 'Resent successfully!', key: 'resend_act', duration: 3 });
      }, 1500);
    }
  };

  // Triggered when "Resend to all" is clicked
  const handleResendAll = () => {
    const allEmails = recipients.map(r => r.email);
    if (onResendAll) {
      onResendAll(allEmails);
    } else {
      setLoadingAction(true);
      message.loading({ content: 'Resending to all recipients...', key: 'resend_all' });
      setTimeout(() => {
        const nowStr = new Date().toISOString();
        setRecipients(prev => prev.map(rec => {
          const nextAttempt = rec.history.length + 1;
          return {
            ...rec,
            resent: true,
            resentText: `Resent • Attempt ${nextAttempt}`,
            pipeline: {
              sent: { status: 'success', text: 'Sent' },
              delivery: { status: 'success', text: 'Delivered' }
            },
            timestamp: `Delivered • Just now`,
            timestampColor: '#228a50',
            history: [
              ...rec.history,
              {
                label: `Attempt ${nextAttempt}`,
                detail: `Sent via manual by current_user at ${formatDate(nowStr)} — Delivered at ${formatDate(nowStr)}`,
                color: '#228a50'
              }
            ],
            activities: [
              ...(rec.activities || []),
              {
                attempt: nextAttempt,
                sent_at: nowStr,
                sent_through: 'manual',
                sent_by: 'current_user',
                delivered_at: nowStr
              }
            ]
          };
        }));
        setLoadingAction(false);
        message.success({ content: 'All emails resent successfully!', key: 'resend_all', duration: 3 });
      }, 1500);
    }
  };

  // Toggles the log display for specific recipient
  const toggleHistoryLogs = (id) => {
    setRecipients(prev => prev.map(rec => {
      if (rec.id === id) {
        return { ...rec, showHistoryLogs: !rec.showHistoryLogs };
      }
      return { ...rec, showHistoryLogs: false };
    }));
  };

  // Handles adding a new recipient
  const handleAddRecipientSubmit = () => {
    if (!newEmail || !newEmail.includes('@')) {
      message.error('Please enter a valid email address');
      return;
    }
    const nowStr = new Date().toISOString();
    const newRecipient = {
      id: newEmail,
      email: newEmail,
      resent: false,
      resentText: 'Resent • Attempt 1',
      pipeline: {
        sent: { status: 'success', text: 'Sent' },
        delivery: { status: 'delivering', text: 'Delivering' }
      },
      timestamp: `Delivering • Just now`,
      timestampColor: '#dc9e03',
      showHistoryLogs: false,
      history: [
        {
          label: 'Attempt 1',
          detail: `Sent via manual at ${formatDate(nowStr)} — Delivering`,
          color: '#dc9e03'
        }
      ],
      activities: [
        {
          attempt: 1,
          sent_at: nowStr,
          sent_through: 'manual'
        }
      ]
    };

    setRecipients([...recipients, newRecipient]);
    setIsAddModalOpen(false);
    setNewEmail('');
    message.success('New recipient added successfully!');
  };

  // Renders the icon based on status
  const renderStepIcon = (step, stepData) => {
    if (step === 'sent') {
      return <CheckSquareIcon color="#1890ff" />;
    }
    if (step === 'delivery') {
      if (stepData.status === 'success') return <CheckSquareIcon color="#228a50" />;
      if (stepData.status === 'delivering') return <DeliveringIcon />;
      if (stepData.status === 'bounced') return <LegendBouncedIcon style={{ fontSize: 20 }} />;
      return <UnopenedCircleIcon />;
    }
    return <UnopenedCircleIcon />;
  };

  const getStepColor = (step, stepData) => {
    if (step === 'sent') {
      return '#1890ff';
    }
    if (stepData.status === 'success') return '#228a50';
    if (stepData.status === 'delivering') return '#dc9e03';
    if (stepData.status === 'bounced') return '#ed5857';
    return '#8c8c8c';
  };

  // Calculations for summary stats
  const totalRecipients = recipients.length;
  let deliveredCount = 0;
  let transitCount = 0;
  let bouncedCount = 0;

  recipients.forEach((rec) => {
    const activities = rec.activities || [];
    activities.forEach((act) => {
      if (act.bounced_at) {
        bouncedCount += 1;
      } else if (act.delivered_at || act.delivery_at) {
        deliveredCount += 1;
      } else if (act.sent_at) {
        transitCount += 1;
      }
    });
  });

  return (
    <div style={{ padding: '0px' }}>
      <Spin spinning={loading}>
        <Card 
          bordered={false} 
          bodyStyle={{ padding: '24px 0 12px 0' }}
        >
          {/* Header Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, padding: '0 24px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#262626' }}>Delivery status by recipient</h3>
            </div>
            {bouncedCount > 0 && (
              <Button 
                onClick={handleResendFailed} 
                loading={loadingAction}
                style={{ 
                  color: '#ed5857', 
                  borderColor: '#ffa39e', 
                  backgroundColor: '#fff1f0',
                  borderRadius: '4px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Icon type="reload" style={{ fontSize: 12 }} />
                Resend to {bouncedCount} failed recipient{bouncedCount > 1 ? 's' : ''}
              </Button>
            )}
          </div>

          {/* Recipients List */}
          <div style={{ borderTop: '1px solid #f0f0f0', maxHeight: '400px', overflowY: 'auto' }}>
            {recipients.map((rec) => {
              const line1Color = rec.pipeline.delivery.status === 'success' ? '#228a50' : 
                                 rec.pipeline.delivery.status === 'bounced' ? '#ed5857' : 
                                 rec.pipeline.delivery.status === 'delivering' ? '#dc9e03' : '#d9d9d9';

              return (
                <div key={rec.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {/* Main Row */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '20px 24px', flexWrap: 'wrap' }}>
                    {/* Email & Contact Type */}
                    <div style={{ width: '280px', flexShrink: 0 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}>{rec.email}</span>
                        {rec.resent && (
                          <div style={{ marginTop: 4 }}>
                            <span style={{ 
                              background: '#f0f5ff', 
                              color: '#2f54eb', 
                              border: '1px solid #adc6ff', 
                              padding: '2px 8px', 
                              borderRadius: '12px', 
                              fontSize: '11px',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <Icon type="reload" style={{ fontSize: 10 }} />
                              {rec.resentText}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Pipeline */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '300px', padding: '0 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
                        {/* Step 1: Sent */}
                        <div style={{ textAlign: 'center', width: '60px', flexShrink: 0 }}>
                          {renderStepIcon('sent', rec.pipeline.sent)}
                          <span style={{ display: 'block', fontSize: 12, marginTop: 4, fontWeight: 600, color: getStepColor('sent', rec.pipeline.sent) }}>
                            {rec.pipeline.sent.text}
                          </span>
                        </div>

                        {/* Connecting Line 1 */}
                        <div style={{ flex: 1, height: 2, background: line1Color, margin: '0 -10px' }} />

                        {/* Step 2: Delivered/Delivering/Bounced */}
                        <div style={{ textAlign: 'center', width: '90px', flexShrink: 0 }}>
                          {renderStepIcon('delivery', rec.pipeline.delivery)}
                          <span style={{ display: 'block', fontSize: 12, marginTop: 4, fontWeight: 600, color: getStepColor('delivery', rec.pipeline.delivery) }}>
                            {rec.pipeline.delivery.text}
                          </span>
                        </div>

                      </div>
                    </div>

                    {/* Timestamp & Actions */}
                    <div style={{ width: '220px', textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: rec.timestampColor, marginBottom: 4 }}>
                        {rec.timestamp}
                      </div>
                      <span 
                        onClick={() => {
                          if (rec.history && rec.history.length > 0) {
                            toggleHistoryLogs(rec.id);
                          } else {
                            message.info('No events to display for this recipient.');
                          }
                        }}
                        style={{ 
                          fontSize: 12, 
                          color: '#1890ff', 
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        className="hover-underline"
                      >
                        {rec.showHistoryLogs ? 'Hide event history' : 'View event history'}
                      </span>
                    </div>
                  </div>

                  {/* Attempt / Logs Detail Block */}
                  {rec.showHistoryLogs && rec.history && rec.history.length > 0 && (
                    <div style={{ padding: '0 24px 20px 24px' }}>
                      <div style={{ 
                        backgroundColor: '#fafafa', 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '4px',
                        padding: '16px 20px',
                      }}>
                        {rec.history.map((hist, idx) => (
                          <Row key={idx} style={{ marginBottom: idx < rec.history.length - 1 ? 8 : 0 }}>
                            <Col span={4}>
                              <span style={{ color: hist.color || '#1890ff', fontWeight: 600, fontSize: 12 }}>{hist.label}</span>
                            </Col>
                            <Col span={20}>
                              <span style={{ color: '#262626', fontSize: 12 }}>{hist.detail}</span>
                            </Col>
                          </Row>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer Summary & Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 8px 24px', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#595959' }}>
              <span style={{ fontWeight: 600, color: '#262626' }}>{totalRecipients}</span> recipients • <span style={{ fontWeight: 600, color: '#262626' }}>{deliveredCount}</span> delivered • <span style={{ fontWeight: 600, color: '#262626' }}>{transitCount}</span> Delivering • <span style={{ fontWeight: 600, color: '#262626' }}>{bouncedCount}</span> bounced
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {/* <Button 
                onClick={() => setIsAddModalOpen(true)}
                style={{ 
                  color: '#1890ff', 
                  borderColor: '#91d5ff', 
                  borderRadius: '4px', 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Icon type="plus" style={{ fontSize: 12 }} />
                Add recipient
              </Button> */}
              <Button 
                type="primary" 
                onClick={handleResendAll} 
                loading={loadingAction}
                className='primary'
                style={{ borderRadius: '4px' }}
              >
                <Icon type="mail" style={{ fontSize: 12 }} />
                Resend to all
              </Button>
            </div>
          </div>
        </Card>
      </Spin>

      {/* Legend Block */}
      <div style={{ 
        border: '1px solid #f0f0f0', 
        borderRadius: '6px', 
        padding: '12px 20px', 
        marginTop: 16,
        backgroundColor: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#595959', gap: '8px' }}>
          <CheckSquareIcon style={{ fontSize: 16 }} color="#1890ff" />
          <span>Sent</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#595959', gap: '8px' }}>
          <DeliveringIcon style={{ fontSize: 16 }} />
          <span>Delivering</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#595959', gap: '8px' }}>
          <CheckSquareIcon style={{ fontSize: 16 }} color="#228a50" />
          <span>Delivered</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#595959', gap: '8px' }}>
          <LegendBouncedIcon style={{ fontSize: 16 }} />
          <span>Bounced / failed</span>
        </div>
      </div>

      {/* Add Recipient Modal */}
      <Modal
        title="Add Recipient"
        visible={isAddModalOpen}
        onOk={handleAddRecipientSubmit}
        onCancel={() => setIsAddModalOpen(false)}
        okText="Add"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <Form.Item label="Email Address">
            <Input 
              value={newEmail} 
              onChange={(e) => setNewEmail(e.target.value)} 
              placeholder="e.g. customer@example.com" 
            />
          </Form.Item>
          <Form.Item label="Recipient Role">
            <Input 
              value={newRole} 
              onChange={(e) => setNewRole(e.target.value)} 
              placeholder="e.g. Billing contact" 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Custom Styles for Hover underline */}
      <style>{`
        .hover-underline:hover {
          text-decoration: underline !important;
        }
      `}</style>
    </div>
  );
};

export default InvoiceEmailActivities;
